import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useEffect, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import {
  ContractStatus,
  GetContractsByOrgResponse,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import {
  SubscriptionData,
  SubscriptionState,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import SubscriptionAction from "./subscription-action";
import { updateTrialDate } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import DatePicker from "components/shared/date-picker";
import { DEFAULT_SNACKBAR_DURATION, NO_DISPLAY_DATA } from "constants/index";
import {
  ContractsListContext,
  SubDataContext,
} from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import {
  convertTimestampToDate,
  convertToTimestamp,
  formatDateTimeDisplay,
} from "helpers/time";
import { UrlParamTypes } from "types/org-display";

const ChangeTrialDateZuora = ({
  subscription,
}: {
  subscription: SubscriptionData;
}) => {
  enum endTrialDateOptions {
    setEndDate = 0,
    endImmediately = 1,
  }

  const theme = useTheme();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [openTrialDateDialog, setOpenTrialDateDialog] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [endTrialOption, setEndTrialOption] = useState(endTrialDateOptions[0]);
  const [endTrialDate, setEndTrialDate] = useState<Date>(
    subscription?.trialEndsAt
      ? convertTimestampToDate(subscription.trialEndsAt)
      : new Date()
  );
  const [endTrialDateDisplay, setEndTrialDateDisplay] = useState(
    formatDateTimeDisplay(endTrialDate)
  );
  const [pickerDateDisplay, setPickerDateDisplay] = useState(
    DateTime.fromJSDate(endTrialDate).toFormat("yyyy-MM-dd")
  );
  const [disablePicker, setDisablePicker] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [enableDecoupleTrial, setEnableDecoupleTrial] =
    useState<boolean>(false);

  const { subData, setSubData } = useContext(SubDataContext);
  const { contractsList, setContractsList, getCurrentContract } =
    useContext(ContractsListContext);
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);
  const { hasPermissions } = useContext(PermissionsContext);

  const hasUnrestrictedTrialExtension = hasPermissions([
    ActionType.UPDATE_UNRESTRICTED_TRIAL_DATE,
  ]);
  const currentContract = getCurrentContract(contractsList);

  const minDate = new Date();
  const maxDate = new Date();
  const maxDurationDays = 60;

  if (subscription?.currentPeriodStartedAt) {
    if (maxDurationDays) {
      maxDate.setDate(
        convertTimestampToDate(subscription.currentPeriodStartedAt).getDate() +
          maxDurationDays
      );
    }
  }

  useEffect(() => {
    if (!subscription?.providerSubscriptionId) {
      setEnableDecoupleTrial(true);
    }
  }, [subscription?.providerSubscriptionId]);

  const handleChangeTrialEndDateState = (newTrialEndDate: Timestamp) => {
    if (subData?.subscription) {
      const newSubData = subData.clone();
      if (newSubData.subscription) {
        newSubData.subscription.currentPeriodEndsAt = newTrialEndDate;
      }
      setSubData(newSubData);
    }

    // When the subscription is in trial, expecting to have a single trial contract with the same end date
    if (currentContract && currentContract.status === ContractStatus.TRIALING) {
      currentContract.endDate = newTrialEndDate;
      setContractsList({
        contracts: [currentContract],
      } as GetContractsByOrgResponse);
    }
  };

  const handleSubmit = () => {
    if (!enableDecoupleTrial && endTrialOption === endTrialDateOptions[1]) {
      const currentDateTime = new Date();
      return handleUpdateTrialDateReq(currentDateTime, true);
    }
    handleUpdateTrialDateReq(endTrialDate, false);
  };

  const handleUpdateTrialDateReq = (newTrialDate: Date, endTrial?: boolean) => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    const newTrialTimestamp = convertToTimestamp(newTrialDate);

    updateTrialDate(
      {
        subscriptionId: subscription?.subscriptionId || "",
        organizationId: roleId,
        newTrialEndDate: newTrialTimestamp,
        endTrial,
      },
      callOptionsHeaders(regionCode, subscription?.subscriptionId || "")
    )
      .then((respEndTrialDate) => {
        if (respEndTrialDate) {
          handleChangeTrialEndDateState(respEndTrialDate);
          setOpenTrialDateDialog(false);
          setEndTrialDateDisplay(
            formatDateTimeDisplay(convertTimestampToDate(respEndTrialDate))
          );
          restoreTrialDatePopup();
        }
        setOpenSnackbar(true);
      })
      .catch((error) => {
        setOpenTrialDateDialog(false);
        showError(error);
      })
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  const isButtonDisabled =
    isSubscriptionActionLoading ||
    !(subscription?.state === SubscriptionState.TRIALING) ||
    !subscription?.active ||
    endTrialDate < new Date();

  const handleOptionChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setEndTrialOption(event.target.value as string);

    if (
      event.target.value === endTrialDateOptions[1] &&
      subscription?.currentPeriodEndsAt
    ) {
      setEndTrialDate(convertTimestampToDate(subscription.currentPeriodEndsAt));
      setDisablePicker(true);
      return;
    }

    if (!pickerDateDisplay) {
      return;
    }
    setDisablePicker(false);
  };

  const isSubmitDisabled =
    !DateTime.fromJSDate(endTrialDate).isValid ||
    endTrialDate < minDate ||
    (!hasUnrestrictedTrialExtension && endTrialDate > maxDate);

  const restoreTrialDatePopup = () => {
    if (subscription?.currentPeriodEndsAt) {
      setEndTrialDate(convertTimestampToDate(subscription.currentPeriodEndsAt));
    }

    setDisablePicker(false);
    setEndTrialOption(endTrialDateOptions[0]);
    setPickerDateDisplay(
      DateTime.fromJSDate(endTrialDate).toFormat("yyyy-MM-dd")
    );
  };

  const warnContainerSx = {
    background: "#ffd4d4",
    border: "1px solid #941c1c",
    color: "#941c1c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  const setTrialDateMessage = () => {
    if (hasUnrestrictedTrialExtension) {
      return;
    }

    return (
      <Box width={1} sx={warnContainerSx}>
        <ul>
          <li>
            <Typography>
              You can only set the trial end date up to 60 days from the current
              start date.
            </Typography>
          </li>
          <li>
            <Typography>
              Please raise a Finance DS ticket if a trial extension beyond 60
              days is needed.
            </Typography>
          </li>
        </ul>
      </Box>
    );
  };

  return (
    <React.Fragment>
      <SubscriptionAction
        title={"Trial End Date:"}
        middleElem={
          <React.Fragment>
            <Typography
              sx={{
                mt: 1,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: theme.typography.body2.fontWeight,
              }}
            >
              {endTrialDateDisplay || NO_DISPLAY_DATA}
              <Tooltip
                title="Data from Subscription Document"
                placement="right"
              >
                <IconButton
                  size="small"
                  sx={{ padding: "2px", marginLeft: "4px" }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
          </React.Fragment>
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            disabled={isButtonDisabled}
            fullWidth
            data-testid="btn-change-trial-end-date"
            onClick={() => setOpenTrialDateDialog(true)}
          >
            CHANGE TRIAL END DATE
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Update Trial End Date"
        open={openTrialDateDialog}
        width="md"
        setOpen={setOpenTrialDateDialog}
        disabled={isSubmitDisabled}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        onConfirm={() => {
          handleSubmit();
        }}
        onCancel={() => {
          restoreTrialDatePopup();
        }}
      >
        <Grid container justifyContent="flex-start" spacing={1}>
          <Grid
            item
            xs={12}
            style={{ marginTop: "15px", marginBottom: "10px" }}
          >
            {endTrialOption === endTrialDateOptions[0] ? (
              setTrialDateMessage()
            ) : (
              <Box width={1} sx={warnContainerSx}>
                <Typography paragraph>
                  Ending trial immediately may take 2 to 3 seconds for the
                  change to take effect.
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item>
            <RadioGroup onChange={handleOptionChange} value={endTrialOption}>
              <FormControlLabel
                value={endTrialDateOptions[0]}
                data-testid="set-end-date"
                control={
                  <Tooltip
                    title="Sets trial end date. Date selection range is restricted to 60 days from the current start date."
                    data-testid="set-end-date-tooltip"
                    placement="right"
                  >
                    <Radio />
                  </Tooltip>
                }
                label={
                  <DatePicker
                    data-testid="date-picker-input-field"
                    defaultDateSelection={endTrialDate}
                    disabled={disablePicker}
                    minDate={minDate}
                    maxDate={
                      hasUnrestrictedTrialExtension ? undefined : maxDate
                    }
                    onChange={(date) => {
                      setEndTrialDate(date);
                      setPickerDateDisplay(
                        DateTime.fromJSDate(date).toFormat("yyyy-MM-dd")
                      );
                    }}
                  />
                }
              />

              {!enableDecoupleTrial && (
                  <FormControlLabel
                    value={endTrialDateOptions[1]}
                    data-testid="end-immediately"
                    control={
                      <Tooltip
                        title="Sets the trial end date to now. Customer will be charged immediately."
                        data-testid="end-immediately-tooltip"
                        placement="right"
                      >
                        <Radio />
                      </Tooltip>
                    }
                    label={"End trial immediately"}
                  />
                )}
            </RadioGroup>
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setOpenSnackbar(false)}
        data-testid="snackbar-change-trial-date"
      >
        <Alert severity="success" variant="filled">
          Trial end date updated
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default ChangeTrialDateZuora;
